// TODO: legacy ui components
import { RichTextEditorComponent } from 'app/components/shared/rich-text-editor/rich-text-editor.component';
import { RichTextEditorModule } from 'app/components/shared/rich-text-editor/rich-text-editor.module';

import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18nPluralPipe, NgForOf, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ButtonDirective, ButtonSpacerDirective } from '../../directives/button.directive';
import { FlexSpacerDirective } from '../../directives/flex-spacer.directive';
import { AuthorLineDirective, LoadingListDirective } from '../../directives/list.directive';
import { ModelTypeKeys } from 'app/enums/model-type-keys.enum';
import { Organization } from 'app/models/entities/organization';
import { Unit } from 'app/models/entities/unit';
import { Membership } from 'app/models/entities/membership';
import { FormSubmission } from 'app/models/entities/forms/form-submission';
import { ViolationStatement } from 'app/models/entities/violation-statement';
import { Vendor } from 'app/models/entities/vendors/vendor';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Note } from 'app/models/entities/note';
import { NotesService } from 'app/services/notes/notes.service';
import { GrowlerService } from 'app/services/growler.service';
import { LegfiDatePipeModule } from 'app/pipes/format/legfi-date.module';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { EmptyStateModule } from '../../empty-state/empty-state.module';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { PreventClickDirective } from '../../directives/prevent-click.directive';
import { MemberAdminDialogComponent, MemberAdminDialogInput, MemberAdminDialogOutput } from '../../../memberships/shared/member-admin-dialog/member-admin-dialog.component';

export interface PublishNoteRequest
{
    note: string;
    private: boolean;
    recipientMemberIds: number[];
}

export type ModelWithOrganization = Unit | Membership | FormSubmission | ViolationStatement | Vendor;

@UntilDestroy()
@Component({
    selector: 'app-notes-card',
    templateUrl: './notes-card.component.html',
    standalone: true,
    host: { class: 'notes-card' },
    imports: [
        MatCardModule,
        NgIf,
        MatButtonModule,
        ButtonDirective,
        MatProgressBarModule,
        MatDividerModule,
        MatListModule,
        LoadingListDirective,
        NgForOf,
        MatMenuModule,
        LegfiDatePipeModule,
        MatIconModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatTooltipModule,
        RichTextEditorModule,
        MatSlideToggleModule,
        I18nPluralPipe,
        ButtonSpacerDirective,
        MatDialogModule,
        AuthorLineDirective,
        EmptyStateModule,
        FlexSpacerDirective,
        PreventClickDirective,
    ],
})
export class NotesCardComponent implements OnInit, OnChanges
{
    @Input() modelType: ModelTypeKeys;
    @Input() model: Organization | ModelWithOrganization;
    @Input() isSaving = false;
    @Input() isOwner = false;

    @ViewChild('editorContainer') editorContainerRef: ElementRef;
    @ViewChild(RichTextEditorComponent) editorRef: RichTextEditorComponent;

    formGroup = this._fb.group({
        id: null,
        note: this._fb.nonNullable.control('', Validators.required),
        private: false,
        sendNotification: false,
        recipients: this._fb.nonNullable.control([]),
    });
    loadingInitialData = true;
    refreshingData = false;
    addNoteEnabled = false;
    notes: Note[];

    recipientsMapping: { [prop: string]: string; } = {
        '=0': 'No recipients',
        '=1': '1 recipient',
        'other': '# recipients'
    };

    constructor(
            private _notesService: NotesService,
            private _fb: FormBuilder,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get hasSelectedRecipients() {
        return this.formGroup.get('recipients').value.length > 0;
    }

    get isViolationOrSubmissionNote() {
        return this.modelType === ModelTypeKeys.FORM_SUBMISSION || this.modelType === ModelTypeKeys.VIOLATION;
    }

    get isSubmissionNote() {
        return this.modelType === ModelTypeKeys.FORM_SUBMISSION;
    }

    get isViolationNote() {
        return this.modelType === ModelTypeKeys.VIOLATION;
    }

    getNoteDetails(note: Note) {
        const details = [];
        if (note.private) {
            details.push('private to me');
        }

        if (!note.updatedAt.isSame(note.createdAt)) {
            details.push('edited');
        }

        if (details.length === 0) {
            return '';
        }

        return '(' + details.join(' • ') + ')';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.hasOwnProperty('model') && this.model) {
            this.refreshNotes();
        }
    }

    ngOnInit() {
        this.formGroup.get('sendNotification').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: (value) => {
                if (value) {
                    this.showNotificationMemberSelector();
                } else {
                    this.formGroup.get('recipients').reset();
                    this.formGroup.get('private').enable();
                }
            },
        });
    }

    refreshNotes() {
        this.refreshingData = true;
        const orgId = this.modelType === ModelTypeKeys.ORGANIZATION ? this.model.id : (this.model as ModelWithOrganization).organizationId;
        this._notesService.getNotes(this.model.id, this.modelType, orgId).pipe(
                finalize(() => {
                    this.loadingInitialData = this.refreshingData = false;
                    this.formGroup.get('id').reset();
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.notes = res || [],
            error: () => this._growler.oops('Unable to load notes'),
        });
    }

    showNotificationMemberSelector(): void {
        if (!this.isViolationOrSubmissionNote) {
            return;
        }

        const model = this.model as FormSubmission|ViolationStatement;
        const data: MemberAdminDialogInput = {
            title: 'Select Recipients',
            required: false,
            hideMissingEmail: true,
            selectedMembershipIds: this.formGroup.get('recipients').value,
            orgId: model.organizationId,
            unitId: model.unitId,
            confirmButton: {
                '=0': 'Select Recipients',
                '=1': 'Select 1 Recipient',
                other: 'Select # Recipients',
            },
            emptyStateLabel: 'No recipients so far...',
        };

        this._dialog.open(MemberAdminDialogComponent, {
            width: MatDialogSizes.MD,
            data,
            autoFocus: 'dialog',
            panelClass: ['v2-dialog', 'selector-layout'],
        }).afterClosed().subscribe((result: MemberAdminDialogOutput) => {
            if (result?.success) {
                if (result?.selectedMemberships.length > 0) {
                    this.formGroup.get('recipients').setValue(result.selectedMemberships.map((m) => m.id));
                    this.formGroup.get('private').reset(false, { disabled: true });
                } else {
                    this.formGroup.get('sendNotification').reset(false);
                }
            }
        });
    }

    addNote() {
        this.addNoteEnabled = true;
        setTimeout(() => {
            this.editorContainerRef.nativeElement.scrollIntoView();
            this.editorRef.focus();
        }, 800);
    }

    editNote(noteId: number) {
        const note = this.notes.find(n => n.id === noteId);
        this.formGroup.reset({
            id: note.id,
            note: note.note,
            private: note.private,
            sendNotification: false,
            recipients: [],
        });

        this.addNote();
    }

    cancelEdit() {
        this.addNoteEnabled = false;

        this.formGroup.reset({
            id: null,
            note: '',
            private: false,
            sendNotification: false,
            recipients: [],
        });
    }

    deleteNote(noteId: number) {
        let type = 'item';
        switch (this.modelType) {
            case ModelTypeKeys.FORM_SUBMISSION:
                type = 'submission';
                break;
            case ModelTypeKeys.VIOLATION:
                type = 'violation';
                break;
        }

        const data: ConfirmActionV2DialogInput = {
            title: `Delete note for this ${type}?`,
            content: 'This action cannot be undone.',
            confirmButton: 'Yes, delete note',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.addNoteEnabled = false;
                    this.formGroup.get('id').setValue(noteId);

                    const orgId = this.modelType === ModelTypeKeys.ORGANIZATION ? this.model.id : (this.model as ModelWithOrganization).organizationId;
                    this._notesService.deleteNote(noteId, orgId).pipe(untilDestroyed(this)).subscribe({
                        next: () => {
                            this._growler.success('Success', 'Note deleted');
                            this.refreshNotes();
                        },
                        error: () => {
                            this._growler.oops('Unable to delete note');
                            this.formGroup.get('id').reset(null);
                        },
                    });
                }
            }
        });
    }

    publishNote(noteId?: number) {
        const request: PublishNoteRequest = {
            note: this.formGroup.get('note').value,
            private: this.formGroup.get('private').value,
            recipientMemberIds: this.formGroup.get('recipients').value,
        };
        const orgId = this.modelType === ModelTypeKeys.ORGANIZATION ? this.model.id : (this.model as ModelWithOrganization).organizationId;

        let obs: Observable<Note>;
        if (noteId) {
            obs = this._notesService.updateNote(noteId, request, orgId);
        } else {
            obs = this._notesService.addNote(this.model.id, this.modelType, request, orgId);
        }

        this.isSaving = true;
        obs.pipe(
                finalize(() => this.isSaving = false),
                untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.formGroup.reset({
                    id: null,
                    note: '',
                    private: false,
                    sendNotification: false,
                    recipients: [],
                });

                this._growler.success('Success', noteId ? 'Note updated' : 'Note published');
                this.refreshNotes();
            },
            error: () => this._growler.oops('Unable to publish this note'),
        });
    }
}
